import { clearToken, getToken } from "./auth";
import type {
  ApiKey,
  ApiKeyCreated,
  ApiKeyScope,
  Attachment,
  Backlink,
  ChatMessage,
  ChatSession,
  Folder,
  GraphData,
  NoteDetail,
  NoteLink,
  NoteRevision,
  NoteSummary,
  NoteType,
  ParaType,
  Project,
  SearchHit,
  SearchMode,
  Tag,
  Task,
  TaskStatus,
  User,
} from "./types";

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { ifMatch?: number } = {},
): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.ifMatch !== undefined) headers.set("If-Match", String(init.ifMatch));

  const res = await fetch(path, { ...init, headers });

  if (res.status === 401) {
    clearToken();
    throw new ApiError(401, "Unauthenticated");
  }
  if (res.status === 204) return undefined as unknown as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = data?.detail ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return data as T;
}

export const api = {
  // Auth
  me: () => request<User>("/api/users/me"),

  // Notes
  listNotes: (
    params: {
      q?: string;
      type?: NoteType;
      archived?: boolean;
      limit?: number;
      folder_id?: string | null;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.type) qs.set("type", params.type);
    if (params.archived) qs.set("archived", "true");
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.folder_id) qs.set("folder_id", params.folder_id);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<NoteSummary[]>(`/api/notes${suffix}`);
  },
  getNote: (id: string) => request<NoteDetail>(`/api/notes/${id}`),
  createNote: (body: { title: string; content_md?: string; type?: NoteType; tags?: string[] }) =>
    request<NoteDetail>("/api/notes", { method: "POST", body: JSON.stringify(body) }),
  updateNote: (
    id: string,
    body: { title?: string; content_md?: string; type?: NoteType; tags?: string[]; folder_id?: string | null },
    version: number,
  ) =>
    request<NoteDetail>(`/api/notes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      ifMatch: version,
    }),
  deleteNote: (id: string) => request<void>(`/api/notes/${id}`, { method: "DELETE" }),
  archiveNote: (id: string) =>
    request<NoteSummary>(`/api/notes/${id}/archive`, { method: "POST" }),

  listOutgoingLinks: (id: string) => request<NoteLink[]>(`/api/notes/${id}/links`),
  listBacklinks: (id: string) => request<Backlink[]>(`/api/notes/${id}/backlinks`),
  listRevisions: (id: string, limit = 20) =>
    request<NoteRevision[]>(`/api/notes/${id}/revisions?limit=${limit}`),

  // Daily / Templates
  getOrCreateDaily: (date: string) =>
    request<NoteDetail>(`/api/notes/daily/${date}`),

  // Folders
  listFolders: () => request<Folder[]>("/api/folders"),
  createFolder: (body: { name: string; parent_id?: string | null; position?: number }) =>
    request<Folder>("/api/folders", { method: "POST", body: JSON.stringify(body) }),
  updateFolder: (id: string, body: { name?: string; parent_id?: string | null; position?: number }) =>
    request<Folder>(`/api/folders/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteFolder: (id: string) => request<void>(`/api/folders/${id}`, { method: "DELETE" }),

  // Tags
  listTags: () => request<Tag[]>("/api/tags"),
  createTag: (body: { name: string; color?: string | null }) =>
    request<Tag>("/api/tags", { method: "POST", body: JSON.stringify(body) }),

  // Search
  search: (q: string, mode: SearchMode = "fts", limit = 20) => {
    const qs = new URLSearchParams({ q, mode, limit: String(limit) });
    return request<SearchHit[]>(`/api/search?${qs}`);
  },

  // Graph
  getGraph: (root?: string, depth = 2) => {
    const qs = new URLSearchParams({ depth: String(depth) });
    if (root) qs.set("root", root);
    return request<GraphData>(`/api/graph?${qs}`);
  },

  // Chat
  listChatSessions: () => request<ChatSession[]>("/api/chat/sessions"),
  createChatSession: (title?: string) =>
    request<ChatSession>("/api/chat/sessions", {
      method: "POST",
      body: JSON.stringify({ title }),
    }),
  deleteChatSession: (id: string) =>
    request<void>(`/api/chat/sessions/${id}`, { method: "DELETE" }),
  listChatMessages: (sessionId: string) =>
    request<ChatMessage[]>(`/api/chat/sessions/${sessionId}/messages`),

  // Attachments
  listAttachments: (noteId?: string) => {
    const qs = noteId ? `?note_id=${noteId}` : "";
    return request<Attachment[]>(`/api/attachments${qs}`);
  },
  uploadAttachment: async (file: File, noteId?: string): Promise<Attachment> => {
    const token = getToken();
    const fd = new FormData();
    fd.append("file", file);
    if (noteId) fd.append("note_id", noteId);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch("/api/attachments", {
      method: "POST",
      headers,
      body: fd,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ detail: "upload failed" }));
      throw new Error(typeof data.detail === "string" ? data.detail : "upload failed");
    }
    return res.json();
  },
  deleteAttachment: (id: string) =>
    request<void>(`/api/attachments/${id}`, { method: "DELETE" }),

  // Clip
  clipUrl: (url: string, opts: { title?: string; selection?: string } = {}) =>
    request<NoteDetail>("/api/clip", {
      method: "POST",
      body: JSON.stringify({ url, ...opts }),
    }),

  // Projects (PARA)
  listProjects: (para_type?: ParaType) => {
    const qs = para_type ? `?para_type=${para_type}` : "";
    return request<Project[]>(`/api/projects${qs}`);
  },
  createProject: (body: { name: string; para_type?: ParaType; color?: string | null }) =>
    request<Project>("/api/projects", { method: "POST", body: JSON.stringify(body) }),
  updateProject: (
    id: string,
    body: { name?: string; para_type?: ParaType; status?: string | null; color?: string | null },
  ) =>
    request<Project>(`/api/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteProject: (id: string) => request<void>(`/api/projects/${id}`, { method: "DELETE" }),

  // Tasks
  listTasks: (params: { status?: TaskStatus; project_id?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status_filter", params.status);
    if (params.project_id) qs.set("project_id", params.project_id);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<Task[]>(`/api/tasks${suffix}`);
  },
  createTask: (body: {
    title: string;
    status?: TaskStatus;
    priority?: number;
    due_at?: string | null;
    project_id?: string | null;
    note_id?: string | null;
  }) =>
    request<Task>("/api/tasks", { method: "POST", body: JSON.stringify(body) }),
  updateTask: (
    id: string,
    body: {
      title?: string;
      status?: TaskStatus;
      priority?: number;
      due_at?: string | null;
      project_id?: string | null;
    },
  ) => request<Task>(`/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  setTaskStatus: (id: string, status: TaskStatus) =>
    request<Task>(`/api/tasks/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  deleteTask: (id: string) => request<void>(`/api/tasks/${id}`, { method: "DELETE" }),

  // API Keys
  listApiKeys: () => request<ApiKey[]>("/api/keys"),
  createApiKey: (body: { name: string; scopes: ApiKeyScope[] }) =>
    request<ApiKeyCreated>("/api/keys", { method: "POST", body: JSON.stringify(body) }),
  revokeApiKey: (id: string) => request<void>(`/api/keys/${id}`, { method: "DELETE" }),

  // Vault (Zero-Knowledge — Server sieht nur Ciphertexts)
  getVaultMeta: () => request<VaultMetaDto>("/api/vault/meta"),
  initVault: (body: VaultInitDto) =>
    request<VaultMetaDto>("/api/vault/init", { method: "POST", body: JSON.stringify(body) }),
  destroyVault: () => request<void>("/api/vault/destroy", { method: "POST" }),
  listSecrets: () => request<SecretListItemDto[]>("/api/vault/secrets"),
  getSecret: (id: string) => request<SecretReadDto>(`/api/vault/secrets/${id}`),
  createSecret: (body: SecretCreateDto) =>
    request<SecretReadDto>("/api/vault/secrets", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateSecret: (id: string, body: Partial<SecretCreateDto>) =>
    request<SecretReadDto>(`/api/vault/secrets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteSecret: (id: string) =>
    request<void>(`/api/vault/secrets/${id}`, { method: "DELETE" }),
};

export interface VaultMetaDto {
  initialized: boolean;
  kdf_algo: string;
  kdf_iterations: number;
  kdf_salt: string;
  verifier: string;
  verifier_iv: string;
}

export interface VaultInitDto {
  kdf_algo: string;
  kdf_iterations: number;
  kdf_salt: string;
  verifier: string;
  verifier_iv: string;
}

export interface SecretListItemDto {
  id: string;
  name: string;
  category: string | null;
  created_at: string;
  updated_at: string;
}

export interface SecretReadDto extends SecretListItemDto {
  encrypted_dek: string;
  encrypted_dek_iv: string;
  encrypted_body: string;
  encrypted_body_iv: string;
}

export interface SecretCreateDto {
  name: string;
  category?: string | null;
  encrypted_dek: string;
  encrypted_dek_iv: string;
  encrypted_body: string;
  encrypted_body_iv: string;
}

export { ApiError };
